import React, { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import "./AddGeoFences.css";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import ThinFooter from "../Footer/ThinFooter";
import ContentWrapper from "../Atomics/ContentWrapper/ContentWrapper";
import axiosInstance from "../../axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import { useHistory } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";

const DEFAULT_COORDINATES = [37.7749, -122.4194]; // Default location (San Francisco)

const RaceGeoFencesMap = () => {
  const history = useHistory();
  const [tracks, setTracks] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [trackDetails, setTrackDetails] = useState({
    trackId: "",
    geoMapName: "",
    configurationId: "",
    cameras: [],
  });
  const [map, setMap] = useState(null);
  const [drawnItems, setDrawnItems] = useState(new L.FeatureGroup());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTracks();
    fetchConfigurations();
  }, []);

  // Fetch tracks from API
  const fetchTracks = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get("/track", {
        headers: {
          Authorization: token,
        },
      });

      if (response.status === 200 && response.data.success) {
        setTracks(response.data.data);
      } else {
        toast.error("Failed to fetch tracks.");
      }
    } catch (error) {
      console.error("Error fetching tracks:", error);
      toast.error("Failed to fetch tracks.");
    } finally {
      setLoading(false);
      initializeMap(...DEFAULT_COORDINATES, "Default Location");
    }
  };

  // Fetch configurations from API
  const fetchConfigurations = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axiosInstance.get("/config", {
        headers: {
          Authorization: token,
        },
      });

      if (response.status === 200 && response.data.success) {
        setConfigurations(response.data.data);
      } else {
        setConfigurations([]);
        toast.error("No configurations found.");
      }
    } catch (error) {
      console.error("Error fetching configurations:", error);
      toast.error("Failed to fetch configurations.");
    }
  };

  // Initialize map
  const initializeMap = (latitude, longitude, title) => {
    if (!map) {
      const mapInstance = L.map("map", {
        center: [latitude, longitude],
        zoom: 12,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(mapInstance);

      mapInstance.addLayer(drawnItems);

      const drawControl = new L.Control.Draw({
        edit: {
          featureGroup: drawnItems,
        },
        draw: {
        polyline: false,
        polygon: true,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: true, // Disable circle markers
        },
      });

      mapInstance.addControl(drawControl);

      mapInstance.on(L.Draw.Event.CREATED, (event) => {
        const { layer } = event;
        drawnItems.addLayer(layer);
        const geoJsonData = layer.toGeoJSON();
        setTrackDetails((prevDetails) => ({
          ...prevDetails,
          cameras: [...prevDetails.cameras, geoJsonData.geometry.coordinates],
        }));
      });

      setMap(mapInstance);
    } else {
      map.setView([latitude, longitude], 12);
    }
  };

  // Handle track selection
  const handleTrackChange = async (e) => {
    const trackId = e.target.value;
    setTrackDetails((prevDetails) => ({ ...prevDetails, trackId }));

    const selectedTrack = tracks.find((track) => track.trackId === parseInt(trackId, 10));
    if (selectedTrack) {
      const { address } = selectedTrack;
      const coordinates = await fetchCoordinates(address);

      if (coordinates) {
        map.setView([coordinates.lat, coordinates.lon], 15);
      } else {
        toast.error("Failed to locate track on the map.");
      }
    }
  };

  const fetchCoordinates = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();

      if (data.length > 0) {
        return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
      }
      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    const { trackId, geoMapName, configurationId, cameras } = trackDetails;
    if (!trackId || !geoMapName || !configurationId || cameras.length === 0) {
      toast.error("Please fill out all fields and draw at least one polygon.");
      return;
    }

    const geoFenceData = {
      trackId: parseInt(trackId, 10),
      geoMapName,
      configurationId: parseInt(configurationId, 10),
      cameras,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post("/geoFence/add", geoFenceData, {
        headers: {
          Authorization: token,
        },
      });

      if (response.status === 200 && response.data.success) {
        toast.success("GeoFence saved successfully!");
        history.push("/geo-fences");
      } else {
        toast.error(response.data.error?.message || "Error saving GeoFence.");
      }
    } catch (error) {
      console.error("Error saving GeoFence:", error);
      toast.error("Error saving GeoFence. Please try again.");
    }
  };

  return (
    <>
      <ContentWrapper>
        <Container fluid className="pb-0">
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/track">Tracks</Breadcrumb.Item>
            <Breadcrumb.Item active>Add Geo Fences</Breadcrumb.Item>
          </Breadcrumb>
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Row className="add_trck_form">
                <Col xs={12} md={5}>
                  <div className="form-group">
                    <label htmlFor="trackName">Track Name</label>
                    <select
                      className="form-control"
                      id="trackName"
                      value={trackDetails.trackId}
                      onChange={handleTrackChange}
                    >
                      <option value="">Select Track Name</option>
                      {tracks.map((track) => (
                        <option key={track.trackId} value={track.trackId}>
                          {track.trackName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col xs={12} md={5}>
                  <div className="form-group">
                    <label htmlFor="geoMapName">Geo Map Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="geoMapName" autocomplete="off"
                      placeholder="Enter Geo Map Name"
                      value={trackDetails.geoMapName}
                      onChange={(e) =>
                        setTrackDetails((prevDetails) => ({
                          ...prevDetails,
                          geoMapName: e.target.value,
                        }))
                      }
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={5}>
                  <div className="form-group">
                    <label htmlFor="configurationName">Configuration Name</label>
                    <select
                      className="form-control"
                      id="configurationName"
                      value={trackDetails.configurationId}
                      onChange={(e) =>
                        setTrackDetails((prevDetails) => ({
                          ...prevDetails,
                          configurationId: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Configuration Name</option>
                      {configurations.map((config) => (
                        <option key={config.configurationId} value={config.configurationId}>
                          {config.configurationName}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12}>
                  <label htmlFor="Geo_Fences">Geo Fences</label>
                  <div id="map" style={{ height: "700px", width: "100%" }}></div>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col xs={12} className="text-right">
                  <button onClick={handleSaveChanges} className="btn btn-primary sv_btn spc_mrg">
                    Save Changes
                  </button>
                </Col>
              </Row>
            </>
          )}
        </Container>
        <ThinFooter />
      </ContentWrapper>
      <Toaster />
    </>
  );
};

export default RaceGeoFencesMap;
